import React, { useEffect } from 'react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { Button, Center, Flex, HStack, Stack, Text, Tooltip, useMediaQuery } from '@chakra-ui/react'
import StateBadge from 'components/StateBadge'
import { EStatus } from 'constants/workplan'
import SvgIcon from 'components/SvgIcon'
import logo from 'assets/images/howfactory.png'
import logoSm from 'assets/images/logo.png'
import classNames from 'classnames'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import startCase from 'lodash/startCase'
import { observer } from 'mobx-react'
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom'
import { toggleFavorite } from 'API/favorite'
import HeaderBar from 'components/HeaderBar'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { HEADER_TITLE } from 'constants/index'
import CompanyPageHeader from 'pages/CompanyDetailPage/components/CompanyPageHeader'
import OrganizationDetailPageHeader from 'pages/OrganizationDetailPage/OrganizationDetailPageHeader'
import OrganizationReportHeader from 'pages/OrganizationReportPage/OrganizationReportHeader'
import routes from 'routes'
import UserPageHeader from '../../pages/GroupUserPage/components/UserPageHeader'
import NotificationBell from './components/NotificationBell'
import FCMStatus from 'components/FCMStatus'
import ProfileDropdown from './components/ProfileDropdown'
import SearchDropdown from './components/SearchDropdown'
import TopbarSearch from './components/TopbarSearch'
import { profileMenus } from './constants'
import styles from './styles.module.scss'
import MessageButton from './components/MessageButton'
import FirebaseDebugger from 'components/FirebaseDebugger'

type TopbarProps = {
  hideLogo?: boolean
  showSidebarMobile: boolean
  navCssClasses?: string
  toggleSidebarCollapse: () => void
  setShowSidebarMobile: () => void
}

const Topbar = (props: TopbarProps) => {
  const hideLogo = props.hideLogo || false
  const navCssClasses = props.navCssClasses || ''
  const containerCssClasses = !hideLogo ? 'container-fluid' : ''
  const location = useLocation()
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  const { processStore, searchStore, favoriteStore, userStore, collectionStore, reportStore, planStore, organizationStore } = useStores()
  const navigate = useNavigate()
  const { process } = processStore
  const { userDetail } = userStore
  const { searchText, totalResults } = searchStore
  const { collection } = collectionStore
  const { reportDetail, companyReportDetail } = reportStore
  const pathname = location?.pathname
  const isMatchedProcessDetail = !!matchPath('/processes/:processId', pathname)
  const isMatchedGroupMemberDetail = !!matchPath('/groups/:groupId', pathname)
  const isMatchedProcessList = !!matchPath(routes.processes.value, pathname) && !isMatchedProcessDetail
  const isMatchedArchivePage = !!matchPath(routes.archive.value, pathname)
  const isMatchedUserDetailPage = !!matchPath('/users/:userId', pathname)
  const isMatchedCommonLibraryPage = !!matchPath(routes.commonLibrary.value, pathname)
  const isMatchedSearchPage = !!matchPath(routes.search.value, pathname)
  const isMatchedGroupPage = !!matchPath(routes.groups.value, pathname) && !isMatchedGroupMemberDetail
  const isMatchedUserPage = !!matchPath(routes.users.value, pathname) && !isMatchedUserDetailPage
  const isMatchedCompanyDetail = !!matchPath(`${routes.admin.report.value}/:organizationId`, pathname)
  const isMatchedOrganizationReportDetail = !!matchPath(`${routes.report.organization.value}/:id`, pathname)
  const isMatchedOrganizationReport =
    !!matchPath(routes.report.organization.value, pathname) && !isMatchedOrganizationReportDetail
  const isMatchedCollectionDetail = !!matchPath(`${routes.collections.value}/:collectionId`, pathname)
  const isMatchedCollectionPage = !!matchPath(routes.collections.value, pathname) && !isMatchedCollectionDetail
  const isMatchedWorkPlanDetail = !!matchPath(`${routes.plans.value}/:id`, pathname)
  const isMatchedUserWorkplanDetail = !!matchPath(`/station/:id`, pathname)
  const isMatchedPlansPage = !!matchPath(routes.plans.value, pathname) && !isMatchedWorkPlanDetail
  // Org setting: hide Work Plans header bars when disabled (undefined/null = enabled for backward compat)
  const isWorkPlansEnabled = organizationStore.organization?.isWorkPlanEnabled !== false
  const isProcessArchived = process?.archivedAt && process?.archivedAt !== null
  const [isProcessFavorite, setIsProcessFavorite] = React.useState<boolean>(false)
  /**
   * Opens the left menu - mobile
   */
  function openLeftMenu() {
    props.setShowSidebarMobile()
    if (document.body) document.body.classList.toggle('sidebar-enable')
  }

  function gotoInboxPage(): void {
    navigate(routes.messages.value)
  }

  function gotoSearchPage(): void {
    searchStore.clearSearchResults()
    searchStore.setManageMode(false)
    searchStore.clearSearchFilter()
    searchStore.setDisplayTypes([EEsGlobalSearchCollection.PROCESS])
    searchStore.setSearchText('')
    searchStore.changeSearchFilter({
      collections: [],
      documentTypes: [],
      groups: [],
      sort: ''
    })
    searchStore.search()
    navigate(`${routes.search.value}?searchText=&page=1`)
  }

  function toggleFavoriteProcess(): void {
    setIsProcessFavorite(!isProcessFavorite)
    toggleFavorite({ processId: process?.id })
  }

  useEffect(() => {
    process?.id &&
      favoriteStore.getIsFavorite({ processId: process?.id }).then(res => {
        setIsProcessFavorite(res)
      })
  }, [process])

  useEffect(() => {
    const parts = location.pathname
      .split('/')
      .filter(part => !!part)
      .map(part => startCase(part))

    const lastPartIndex = parts.length - 1
    switch (true) {
      case isMatchedProcessDetail:
        parts.splice(lastPartIndex, 0, process?.name ?? '')
        break
      case isMatchedUserDetailPage:
        parts.splice(lastPartIndex, 0, userDetail?.fullName ?? '')
        break
      case isMatchedCollectionDetail:
        parts.splice(lastPartIndex, 0, collection?.name ?? '')
        break
      case isMatchedOrganizationReportDetail:
        parts.splice(lastPartIndex, 0, reportDetail?.name ?? '')
        break
      case isMatchedCompanyDetail:
        parts.splice(lastPartIndex, 0, companyReportDetail?.name ?? '')
        break
      case isMatchedWorkPlanDetail:
        parts.splice(lastPartIndex, 0, planStore?.selectedWorkplan?.name ?? '')
        break
      case isMatchedUserWorkplanDetail:
        parts.splice(0, parts.length, planStore?.stationDetail?.name ?? '')
        break
    }

    const formattedTitle = parts.join(' - ')
    document.title = `${HEADER_TITLE}` + (formattedTitle ? ` - ${formattedTitle}` : '')
  }, [
    location.pathname,
    process?.name,
    userDetail?.fullName,
    collection?.name,
    reportDetail?.name,
    companyReportDetail?.name,
    planStore?.selectedWorkplan?.name,
    planStore?.workplanDetail?.name,
    planStore?.stationDetail?.lineBalanceName,
    planStore?.stationDetail?.name
  ])

  return (
    <React.Fragment>
      <div className={styles.container}>
        <div className={cx(`navbar-custom ${navCssClasses}`, styles.navSpacing)}>
          <div className={cx(styles.primaryNavSpacing, containerCssClasses)}>
            {!hideLogo && (
              <Link to="/" className="topnav-logo">
                <span className="topnav-logo-lg">
                  <img src={logo} alt="logo" height="16" />
                </span>
                <span className="topnav-logo-sm">
                  <img src={logoSm} alt="logo" height="16" />
                </span>
              </Link>
            )}

            <ul className="list-unstyled topbar-right-menu float-right mb-0">
              <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                <SvgIcon iconName="process-button" size={40} style={{ margin: 'auto 8px', cursor: 'pointer' }} onClick={gotoSearchPage} />
              </li>
              <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                {/* <SvgIcon iconName="inbox-mobile-button" size={24} style={{ cursor: 'pointer' }} onClick={gotoInboxPage} /> */}
                <MessageButton />
              </li>
              <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                <NotificationBell />
              </li>
              <li className="notification-list d-md-none">
                <ProfileDropdown menuItems={profileMenus} />
              </li>
            </ul>

            <button className="button-menu-mobile open-left disable-btn" onClick={openLeftMenu} aria-label="Toggle sidebar" title="Toggle sidebar">
              <i className={classNames('las la-bars', styles.collapseIcon, styles.mobileCollapseIcon)}></i>
            </button>

            <ul className="list-unstyled topbar-right-menu mb-0">
              <li className="notification-list topbar-dropdown d-md-none" style={{ display: 'inline-block' }}>
                <SearchDropdown />
              </li>
              {/* <li style={{ marginTop: 16 }} className="notification-list topbar-dropdown d-md-none">
                <SvgIcon iconName="process-button" size={24} style={{ margin: 'auto 8px', cursor: 'pointer' }} onClick={gotoSearchPage} />
              </li> */}
            </ul>
            {props.showSidebarMobile ? <div className={classNames(styles.backgroundButtonMenuMobile)} /> : ''}

            <div className={styles.leftSideWrapper}>
              <i className={classNames('las la-bars', styles.collapseIcon)} onClick={props.toggleSidebarCollapse}></i>
              <Center width="full" gap={2}>
                <TopbarSearch />
                <Center height="70px">
                  <Tooltip label="My Processes">
                    <Button
                      boxSize={10}
                      background="white"
                      border="1px solid #E2E8F0"
                      padding={0}
                      onClick={gotoSearchPage}
                    >
                      <SvgIcon iconName="process-icon" size={20} style={{ margin: 'auto 0' }} />
                    </Button>
                  </Tooltip>
                </Center>
              </Center>
              <HStack width="290px">
                {/* <SvgIcon iconName="inbox-button" size={24} style={{ cursor: 'pointer' }} onClick={gotoInboxPage} /> */}
                <MessageButton />
                <Flex>
                  <NotificationBell />
                </Flex>
                <ProfileDropdown menuItems={profileMenus} />
              </HStack>
            </div>
          </div>
          {isMatchedProcessDetail && (
            <HeaderBar
              title={
                <Stack flexDirection={'row'} gap={2} spacing={0}>
                  <HStack width="full" color="primary.500">
                    {process?.archivedAt ? (
                      <Text cursor="pointer" onClick={() => navigate(routes.archive.value, { replace: true })}>
                        Archives
                      </Text>
                    ) : (
                      <>
                        {process?.isPublished ? (
                          <Text cursor="pointer" onClick={gotoSearchPage}>
                            My Processes
                          </Text>
                        ) : (
                          <Text cursor="pointer" onClick={() => navigate(routes.processes.value, { replace: true })}>
                            Drafts
                          </Text>
                        )}
                      </>
                    )}
                    <ChevronRightIcon color="gray.700" />
                    {isMobile ? (
                      <Tooltip label={process?.name ?? ''} hasArrow placement="top" openDelay={200} shouldWrapChildren>
                        <Text noOfLines={2} color="gray.700" tabIndex={0}>
                          {process?.name ?? ''}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text noOfLines={1} color="gray.700">
                        {process?.name ?? ''}
                      </Text>
                    )}
                  </HStack>
                  <HStack>
                    <span className={styles.tagContainer}>{`v${process?.version ?? '1.0.0'}`}</span>
                    {process?.isPublished === false ? (
                      <span className={cx(styles.tagContainer, styles.isDraft)}>Draft</span>
                    ) : (
                      <></>
                    )}
                    {isProcessArchived && <span className={cx(styles.tagContainer, styles.archive)}>Archived</span>}
                    {isProcessFavorite ? (
                      <SvgIcon iconName="favorite" size={24} style={{ cursor: 'pointer' }} onClick={toggleFavoriteProcess} />
                    ) : (
                      <SvgIcon iconName="un_favorite" size={24} style={{ cursor: 'pointer' }} onClick={toggleFavoriteProcess} />
                    )}
                  </HStack>
                </Stack>
              }
              controlBy="processDetail"
            />
          )}
          {isMatchedUserDetailPage && (
            <HeaderBar
              title={
                <HStack spacing={1}>
                  <Text
                    color="primary.500"
                    cursor="pointer"
                    onClick={() => navigate(routes.users.value, { replace: true })}
                  >
                    Users
                  </Text>
                  <ChevronRightIcon />
                  <Text color="gray.700">{userDetail?.fullName ?? ''}</Text>
                </HStack>
              }
              controlBy="userDetailPage"
            />
          )}

          {isMatchedProcessList && <HeaderBar title="Drafts" controlBy="processList" />}

          {isMatchedSearchPage && (
            <HeaderBar
              title={
                searchText
                  ? `${totalResults} results for "${searchText}"`
                  : searchStore.countFilter() > 0
                    ? `${totalResults} results`
                    : 'My Processes'
              }
              controlBy={'searchPage'}
            />
          )}

          {isMatchedCollectionPage && <HeaderBar title="Collections" controlBy={'collectionPage'} />}

          {isMatchedCollectionDetail && (
            <HeaderBar
              title={
                <HStack color="primary.500" spacing={1}>
                  {collection?.archivedAt ? (
                    <Text cursor="pointer" onClick={() => navigate(routes.archive.value, { replace: true })}>
                      Archives
                    </Text>
                  ) : (
                    <Text cursor="pointer" onClick={() => navigate(routes.collections.value, { replace: true })}>
                      Collections
                    </Text>
                  )}
                  <ChevronRightIcon color="gray.700" />
                  <Text marginRight="6px !important" color="gray.700">
                    {collection?.name ?? ''}
                  </Text>
                  {collection?.isPublished === false && (
                    <span className={cx(styles.tagContainer, styles.isDraft)}>Draft</span>
                  )}
                </HStack>
              }
              controlBy={'collectionDetailPage'}
            />
          )}

          {isMatchedGroupPage && <HeaderBar title="User Groups" />}

          {isMatchedUserPage && <HeaderBar title="Users" controlBy={'userPage'} />}

          {isMatchedArchivePage && <HeaderBar title="Archives" controlBy="archivePage" />}

          {isMatchedCommonLibraryPage && <HeaderBar title="Common Library" controlBy="commonLibraryList" />}

          {isWorkPlansEnabled && isMatchedPlansPage && <HeaderBar title="Work Plans" controlBy="plansPage" />}
          {isWorkPlansEnabled && isMatchedWorkPlanDetail && (
            <HeaderBar
              title={
                <HStack color="primary.500" spacing={1}>
                  <Text cursor="pointer" onClick={() => navigate(routes.plans.value, { replace: true })}>
                    Work plans
                  </Text>
                  <ChevronRightIcon color="gray.700" />
                  <HStack spacing={2}>
                    <Text marginRight="6px !important" color="gray.700">
                      {planStore?.workplanDetail?.name ?? ''}
                    </Text>
                    {Number(planStore?.workplanDetail?.status) === EStatus.INACTIVE && !planStore?.workplanDetail?.originalLineBalanceId && (
                      <StateBadge status={EStatus.INACTIVE} />
                    )}
                  </HStack>
                </HStack>
              }
              controlBy="workPlanDetail"
            />
          )}

          {isWorkPlansEnabled && isMatchedUserWorkplanDetail && (
            <HeaderBar
              title={
            <Text color="gray.700">{planStore?.stationDetail?.name ?? 'Station'}</Text>
              }
              controlBy="workPlanDetail"
            />
          )}

          {isMatchedGroupMemberDetail && <HeaderBar title={<UserPageHeader />} controlBy="groupMemberList" />}

          {isMatchedCompanyDetail && <HeaderBar title={<CompanyPageHeader />} />}

          {isMatchedOrganizationReport && <HeaderBar title={<OrganizationReportHeader />} />}

          {isMatchedOrganizationReportDetail && <HeaderBar title={<OrganizationDetailPageHeader />} />}
        </div>
      </div>
    </React.Fragment>
  )
}

export default observer(Topbar)
